import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Atom, Binary, ShieldCheck, Microscope } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/index";
import {
  MilestoneCard,
  ApplicationCard,
  InitiativeCard
} from "@/components/Cards";
import {
  ParticleAnimation,
  QuantumWave,
  AtomicStructure
} from "@/components/QuantumVisualizations";
import {
  quantumMilestones,
  quantumApplications,
  bahrainInitiatives
} from "@/data/index";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";

const Home: React.FC = () => {
  const majorMilestones = quantumMilestones.filter((m) => m.isMajor).slice(-3);
  const featuredApplications = quantumApplications.slice(0, 3);
  const primaryInitiatives = bahrainInitiatives.slice(0, 3);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-24">
        <div className="absolute inset-0 z-0">
          <ParticleAnimation className="opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-background/80 to-background" />
        </div>

        <div className="container mx-auto px-4 z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <span className="inline-block py-1 px-3 mb-6 text-xs font-mono tracking-widest uppercase bg-accent/10 text-accent border border-accent/20 rounded-full">
              1925 — 2025: The Quantum Centennial
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight">
              Celebrating <span className="text-primary">100 Years</span> of <br />
              Quantum Physics
            </h1>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              From Heisenberg's matrix mechanics to Bahrain's vision for a quantum-secured future. 
              Witness the transition from theoretical mystery to the second industrial revolution.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8 shadow-lg hover:shadow-primary/20 transition-all">
                <Link to={ROUTE_PATHS.TIMELINE}>
                  Explore the Timeline <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 backdrop-blur-sm">
                <Link to={ROUTE_PATHS.BAHRAIN}>Bahrain's Initiatives</Link>
              </Button>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 w-full">
          <QuantumWave className="opacity-30" />
        </div>
      </section>

      {/* Century of Discovery Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12 mb-20">
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                The Path to <span className="text-accent">Entanglement</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                In 1925, Werner Heisenberg published the first formalization of quantum mechanics. 
                Today, we stand on the precipice of the Second Quantum Revolution, where superposition 
                and entanglement are no longer paradoxes, but the fuel for the next century of innovation.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-card border border-border">
                  <div className="text-primary mb-2"><Atom className="h-8 w-8" /></div>
                  <h4 className="font-bold text-lg">Subatomic Precision</h4>
                  <p className="text-sm text-muted-foreground">Mastering the laws that govern the smallest building blocks of reality.</p>
                </div>
                <div className="p-4 rounded-xl bg-card border border-border">
                  <div className="text-accent mb-2"><Binary className="h-8 w-8" /></div>
                  <h4 className="font-bold text-lg">Computational Power</h4>
                  <p className="text-sm text-muted-foreground">Solving problems that would take classical supercomputers millennia.</p>
                </div>
              </div>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 rounded-full blur-3xl animate-pulse" />
                <AtomicStructure className="w-full max-w-[400px] aspect-square relative z-10" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {majorMilestones.map((milestone) => (
              <MilestoneCard key={milestone.id} milestone={milestone} />
            ))}
          </div>
        </div>
      </section>

      {/* Bahrain's Vision Section */}
      <section className="py-24 bg-secondary/30 relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src={IMAGES.BAHRAIN_SKYLINE_1} 
                  alt="Bahrain Skyline" 
                  className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">The Quantum Hub of the Middle East</h3>
                  <p className="text-white/80">Kingdom of Bahrain is pioneering the integration of quantum-secured networks and AI-driven drug discovery.</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <span className="text-accent font-mono text-sm tracking-widest uppercase mb-4 block">
                National Strategic Vision
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Quantum Sovereignity & <br />
                Bahrain Vision 2030
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Through strategic partnerships with global leaders like SandboxAQ and the local leadership of Mumtalakat and NCSC, 
                Bahrain is securing its digital future. We are building the infrastructure for a post-quantum world today.
              </p>
              <div className="space-y-6">
                {primaryInitiatives.map((initiative) => (
                  <InitiativeCard key={initiative.id} initiative={initiative} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Showcase */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">The Future is <span className="text-primary">Quantum-Powered</span></h2>
            <p className="text-muted-foreground text-lg">
              From medicine to cybersecurity, quantum technology is transforming every industry. Explore how these applications are being deployed in the Kingdom.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredApplications.map((app) => (
              <ApplicationCard key={app.id} application={app} />
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button asChild variant="ghost" className="hover:bg-primary/10">
              <Link to={ROUTE_PATHS.APPLICATIONS}>
                View All Applications <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img src={IMAGES.QUANTUM_COMPUTING_1} alt="Quantum Background" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="bg-primary text-primary-foreground rounded-3xl p-12 md:p-20 text-center shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-background/10 rounded-full blur-3xl -ml-32 -mb-32" />
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                Join the Second Quantum Revolution
              </h2>
              <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
                Be part of the Centennial celebrations in Bahrain. Explore our research, 
                partnerships, and the upcoming Quantum Computing Lab.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Button size="lg" variant="secondary" className="rounded-full px-10 font-bold">
                  Get Involved
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-10 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to={ROUTE_PATHS.FUTURE}>The Future of Quantum</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Grid Stats */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-1">
              <div className="text-3xl font-bold text-primary font-mono">100</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Years of Physics</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-accent font-mono">$1B+</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">SandboxAQ Partnership</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-primary font-mono">2025</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">UNESCO Quantum Year</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-bold text-accent font-mono">Vision</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Bahrain 2030 Ready</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
